import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const errorId = `${textareaId}-error`;
  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={textareaId} className="text-meta text-ink-1 font-medium">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={textareaId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          'min-h-[96px] p-3 text-ui bg-paper-0 text-ink-0 border rounded-token-md',
          'placeholder:text-ink-3',
          'focus:outline-none focus-visible:shadow-focus',
          'disabled:bg-paper-1 disabled:text-ink-3 disabled:cursor-not-allowed',
          'transition-colors duration-2 ease-brand',
          'resize-y',
          error
            ? 'border-status-err focus-visible:border-status-err'
            : 'border-line-2 focus-visible:border-primary',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="text-meta text-status-err">
          {error}
        </p>
      ) : null}
    </div>
  );
});
